import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { TrustAssuranceSection } from './components/TrustAssuranceSection';
import { TrustCtaSection } from './components/TrustCtaSection';
import { TrustFaqSection } from './components/TrustFaqSection';
import { TrustLawsSection } from './components/TrustLawsSection';
import { TrustProfileHero } from './components/TrustProfileHero';
import { TrustShell } from './components/TrustShell';
import { usePublicTrust } from './hooks/usePublicTrust';
import { isPreviewRequested } from './previewSession';

function TrustLoading() {
  return (
    <div className="mx-auto max-w-5xl px-5 sm:px-6 lg:px-8 pt-10 pb-20 text-center lg:pt-12 lg:pb-24">
      <div className="mx-auto mb-4 h-10 w-10 animate-pulse rounded-full bg-primary/20" />
      <p className="font-mono text-sm uppercase tracking-widest text-gray-500">
        Loading trust profile…
      </p>
    </div>
  );
}

function TrustNotFound({ slug }: { slug: string }) {
  return (
    <div className="mx-auto max-w-5xl px-5 sm:px-6 lg:px-8 pt-10 pb-20 text-center lg:pt-12 lg:pb-24">
      <p className="mb-2 font-mono text-6xl font-bold text-gray-700">404</p>
      <h1 className="mb-4 font-display text-2xl font-bold">Trust profile not found</h1>
      <p className="text-gray-400">
        No public AI Trust Center for <code className="font-mono text-primary">{slug}</code>. The slug may
        be wrong or trust visibility is not enabled.
      </p>
    </div>
  );
}

function TrustError() {
  return (
    <div className="mx-auto max-w-5xl px-5 sm:px-6 lg:px-8 pt-10 pb-20 text-center lg:pt-12 lg:pb-24">
      <h1 className="mb-4 font-display text-2xl font-bold">Unable to load trust data</h1>
      <p className="text-gray-400">Try again in a moment. The public trust API may be unavailable.</p>
    </div>
  );
}

/**
 * Shown only to the signed-in tenant previewing an unpublished page. Says what
 * it is and that nobody else can see it; the rest of the page is exactly what
 * would go public (same snapshot builder), so what you see is what you ship.
 */
function TrustPreviewBanner() {
  return (
    <div
      role="status"
      data-testid="trust-preview-banner"
      className="border-b border-red-700 bg-red-600 px-4 py-2.5 text-center text-sm text-white"
    >
      <span className="font-semibold uppercase tracking-wider">Preview</span>
      <span aria-hidden className="mx-2 text-red-200">
        ·
      </span>
      This page is not public yet: only you can see it. Publish your AI Trust Center from the
      dashboard to make it public.
    </div>
  );
}

export function TrustProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const { search } = useLocation();
  const previewRequested = isPreviewRequested(search);
  const { loadState, snapshot, preview } = usePublicTrust(slug, previewRequested);

  // A preview is private: keep crawlers off it even if the URL leaks.
  useEffect(() => {
    if (!preview) {
      return;
    }
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, [preview]);

  // Per-tenant title: this link gets shared in sales cycles and Slack;
  // "Acme, AI Trust Center" beats a generic tab name.
  useEffect(() => {
    if (snapshot) {
      document.title = `${snapshot.display_name ?? slug}, AI Trust Center`;
    }
    return () => {
      document.title = 'ComplyEdge Trust';
    };
  }, [snapshot, slug]);

  if (!slug) {
    return (
      <TrustShell>
        <TrustError />
      </TrustShell>
    );
  }

  if (loadState === 'loading') {
    return (
      <TrustShell>
        <TrustLoading />
      </TrustShell>
    );
  }

  if (loadState === 'not_found') {
    return (
      <TrustShell>
        <TrustNotFound slug={slug} />
      </TrustShell>
    );
  }

  if (loadState === 'error' || !snapshot) {
    return (
      <TrustShell>
        <TrustError />
      </TrustShell>
    );
  }

  const articlesScreened = snapshot.articles_screened ?? [];
  const frameworksScreened = snapshot.frameworks_screened ?? [];

  return (
    <TrustShell
      tenant={{ name: snapshot.display_name ?? slug, websiteUrl: snapshot.website_url }}
    >
      {preview ? <TrustPreviewBanner /> : null}
      <TrustProfileHero slug={slug} snapshot={snapshot} />

      <div className="mx-auto max-w-5xl px-5 sm:px-6 lg:px-8 pt-10 pb-20 lg:pt-12 lg:pb-24">
        <TrustAssuranceSection />

        <TrustLawsSection
          frameworks={frameworksScreened}
          articlesEnforced={snapshot.articles_enforced}
          articlesScreened={articlesScreened}
        />

        <TrustFaqSection />

        <TrustCtaSection />
      </div>
    </TrustShell>
  );
}
